import type { VueGtmUseOptions } from '@gtm-support/vue-gtm'
import { isMasurenPortal } from '~/utils/isMasurenPortal'

export default defineNuxtPlugin(async (nuxtApp) => {
  const config = useRuntimeConfig()

  const route = useRoute()
  const { currentPortal } = usePortals()

  const isHpveTenant = currentPortal.value.portal.tenant === 'hpve'
  const isTravanto = currentPortal.value.portal.portal === 'http://www.travanto.de'
  const isSyltTravel = currentPortal.value.portal.portal === 'http://www.sylt-travel.de'
  const isVermieter = isTravanto && route.path.startsWith('/vermieter/magazin/')
  const isMietwagen = isSyltTravel && route.path.startsWith('/mietwagen/')

  const getGtmId = (): string => {
    if (isHpveTenant) {
      return 'GTM-5TKZ88J' // harz-travel.de and sauerland-travel.de
    }
    if (isVermieter) {
      return 'GTM-KNWD3R' // /vermieter/ (vermieterseite) subportal of travanto.de
    }
    if (isMietwagen) {
      return 'GTM-W26BGZJ' // /mietwagen/ subportal of sylt-travel.de
    }
    return 'GTM-5FXF6X' // Travanto and regional portals
  }

  const getGtmHost = (): string => {
    if (isMietwagen) {
      return 'www.googletagmanager.com'
    }
    return currentPortal.value.gtm_host
  }

  const GtmId = getGtmId()
  const GtmHost = getGtmHost()
  const gtmOptions = {
    LOCAL: {
      googleTagManagerId: 'GTM-0',
      googleTagManagerEnabled: true,
      googleTagManagerDebug: true
    },
    TEST: {
      googleTagManagerId: 'GTM-0',
      googleTagManagerEnabled: true,
      googleTagManagerDebug: true
    },
    STAGING: {
      googleTagManagerId: GtmId,
      googleTagManagerEnabled: true,
      googleTagManagerDebug: false
    },
    PRODUCTION: {
      googleTagManagerId: GtmId,
      googleTagManagerEnabled: true,
      googleTagManagerDebug: false
    }
  }

  if (!Object.prototype.hasOwnProperty.call(gtmOptions, config.public.nuxtEnvironmentStage)) {
    return
  }

  const stageOptions = gtmOptions[config.public.nuxtEnvironmentStage]
  const blockThirdParty = isMasurenPortal(currentPortal.value?.portal?.portal)

  if (blockThirdParty) {
    useHead({
      script: {
        children: 'window.dataLayer = window.dataLayer || [];',
        tagPriority: 'critical'
      }
    })
    return {
      provide: {
        dataLayerPushInHead: () => {}
      }
    }
  }

  const { createGtm } = await import('@gtm-support/vue-gtm')
  nuxtApp.vueApp.use(createGtm({
    id: stageOptions.googleTagManagerId,
    defer: false,
    compatibility: false,
    enabled: stageOptions.googleTagManagerEnabled,
    debug: stageOptions.googleTagManagerDebug,
    loadScript: false,
    vueRouter: useRouter() as VueGtmUseOptions['vueRouter'],
    trackOnNextTick: false
  }))

  useHead({
    script: {
      children: `
  <!-- Google Tag Manager -->
  (function(w,d,s,l,i){
      w[l]=w[l]||[];
      w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});
      var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';
      j.async=true;
      j.src='https://${GtmHost}/gtm.js?id='+i+dl;
      f.parentNode.insertBefore(j,f);
  })(window,document,'script','dataLayer','${GtmId}');
  <!-- End Google Tag Manager -->
`,
      tagPriority: 'critical'
    }
  })

  return {
    provide: {
      dataLayerPushInHead: (data: object) => {
        useHead({
          script: {
            children: `dataLayer.push( ${JSON.stringify(data)} );`,
            tagPriority: 'critical'
          }
        })
      }
    }
  }
})
